import AsyncStorage, { captureAccount } from "./accountStorage";
import NetInfo from "@react-native-community/netinfo";
import openhouseAPI from "./openhouse";

const REQUEST_TIMEOUT_MS = 15_000;
const LOCAL_ID = "local_submission_id";
const DELIVERY_BASELINE = "delivery_baseline_ids";

let storageOperation = Promise.resolve();
const deliveryOperations = new Map<string, Promise<unknown>>();

const withStorageLock = async <T>(operation: () => Promise<T>): Promise<T> => {
  const assertAccount = captureAccount();
  const guarded = () => { assertAccount(); return operation(); };
  const result = storageOperation.then(guarded, guarded);
  storageOperation = result.then(
    () => undefined,
    () => undefined
  );
  return result;
};

const withDeliveryLock = async <T>(
  openHouseUuid: string,
  operation: () => Promise<T>
): Promise<T> => {
  const previous = deliveryOperations.get(openHouseUuid) || Promise.resolve();
  const result = previous.then(operation, operation);
  deliveryOperations.set(openHouseUuid, result);
  try {
    return await result;
  } finally {
    if (deliveryOperations.get(openHouseUuid) === result) {
      deliveryOperations.delete(openHouseUuid);
    }
  }
};

const storageKey = (openHouseUuid: string) => `@openHouse+${openHouseUuid}`;

const readAttendees = async (openHouseUuid: string): Promise<any[]> => {
  const value = await AsyncStorage.getItem(storageKey(openHouseUuid));
  if (!value) return [];
  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? parsed : [];
};

const writeAttendees = (openHouseUuid: string, attendees: any[]) =>
  AsyncStorage.setItem(storageKey(openHouseUuid), JSON.stringify(attendees));

const createLocalId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const apiPayload = (attendee: any) => {
  const { [LOCAL_ID]: _localId, [DELIVERY_BASELINE]: _baseline, is_synced: _isSynced, ...payload } = attendee;
  return payload;
};

const sendAttendee = async (
  openHouseUuid: string,
  primaryAgentUserId: string | number,
  attendee: any,
  assertAccount: () => void
) => {
  assertAccount();
  const response = await openhouseAPI.put(
    `/openhouse/${openHouseUuid}/attendee/${primaryAgentUserId}`,
    apiPayload(attendee),
    { timeout: REQUEST_TIMEOUT_MS, accountGuard: assertAccount } as any
  );

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Unexpected response status: ${response.status}`);
  }
};

const markSynced = (openHouseUuid: string, localId: string, serverUuid?: string) =>
  withStorageLock(async () => {
    const attendees = await readAttendees(openHouseUuid);
    const updated = attendees.map((attendee) =>
      attendee?.[LOCAL_ID] === localId
        ? { ...attendee, is_synced: true, ...(serverUuid ? { uuid: serverUuid } : {}) }
        : attendee
    );
    await writeAttendees(openHouseUuid, updated);
    return updated;
  });

// A lost acknowledgement is ambiguous: capture server IDs before delivery,
// then reconcile a matching new record before replaying the same submission.
// Server-side idempotency is still needed for a strict exactly-once guarantee.
const remoteAttendees = async (uuid: string, assertAccount: () => void): Promise<any[]> => {
  const response = await openhouseAPI.get(`/openhouse/${uuid}/attendeesAndTags`, {
    timeout: REQUEST_TIMEOUT_MS, accountGuard: assertAccount,
  } as any);
  const attendees = response.data?.data?.attendees;
  if (!Array.isArray(attendees)) throw new Error("Unable to verify saved leads");
  return attendees;
};
const identity = (attendee: any) => [
  String(attendee.first_name || "").trim().toLowerCase(),
  String(attendee.last_name || "").trim().toLowerCase(),
  String(attendee.email || "").trim().toLowerCase(),
  String(attendee.phone || "").replace(/\D/g, ""),
];
const matches = (local: any, remote: any) => {
  const fields = identity(local);
  const other = identity(remote);
  return fields.every(Boolean) && fields.every((value, index) => value === other[index]);
};

// Staging can return an acknowledged lead before contact enrichment finishes.
// Names must match, and every contact field actually present must agree.
const matchesAcknowledged = (local: any, remote: any) => {
  const fields = identity(local), other = identity(remote);
  return Boolean(fields[0] && fields[1]) && fields[0] === other[0] && fields[1] === other[1] &&
    other.slice(2).every((value, index) => !value || value === fields[index + 2]);
};

// Screen refreshes share the same storage lock as submissions. Keep local
// delivery metadata even when the server list is refreshed during an upload.
export const cacheRemoteAttendees = (uuid: string, remote: any[]) =>
  withStorageLock(async () => {
    const stored = await readAttendees(uuid);
    const available = remote.map(attendee => ({ ...attendee, is_synced: true }));
    const local: any[] = [];
    // Match the most constrained local records first so identical intentional
    // submissions each consume their own server representation.
    const ordered = [...stored].sort((a, b) =>
      Number(Boolean(b.uuid)) - Number(Boolean(a.uuid)) ||
      (b[DELIVERY_BASELINE]?.length || 0) - (a[DELIVERY_BASELINE]?.length || 0));
    for (const attendee of ordered) {
      if (!attendee?.is_synced && !attendee?.uuid) {
        local.push({ ...attendee, [LOCAL_ID]: attendee[LOCAL_ID] || createLocalId() });
        continue;
      }
      if (!attendee?.[LOCAL_ID]) continue;
      // A confirmed server record removed upstream should disappear offline.
      // Pending or acknowledged-but-unidentified submissions remain recoverable.
      if (attendee.is_synced && attendee.uuid &&
          !available.some(entry => entry.uuid === attendee.uuid)) continue;
      const candidates = available.filter(entry => attendee.uuid
        ? entry.uuid === attendee.uuid
        : Array.isArray(attendee[DELIVERY_BASELINE]) &&
          !attendee[DELIVERY_BASELINE].includes(entry.uuid) && matchesAcknowledged(attendee, entry));
      const index = candidates.length === 1 ? available.indexOf(candidates[0]) : -1;
      if (index >= 0) {
        const [entry] = available.splice(index, 1);
        local.push({ ...attendee, ...entry,
          email: entry.email || attendee.email,
          phone: entry.phone || attendee.phone,
        });
      } else {
        local.push(attendee);
      }
    }
    const updated = [...local, ...available];
    await writeAttendees(uuid, updated);
  });

export const persistAttendee = (
  openHouseUuid: string,
  attendee: any
): Promise<{ attendee: any; attendees: any[] }> =>
  withStorageLock(async () => {
    const attendees = await readAttendees(openHouseUuid);
    const storedAttendee = {
      ...attendee,
      is_synced: attendee?.is_synced === true,
      [LOCAL_ID]: attendee?.[LOCAL_ID] || createLocalId(),
    };
    const updated = [...attendees, storedAttendee];
    await writeAttendees(openHouseUuid, updated);
    return { attendee: storedAttendee, attendees: updated };
  });

export const submitOpenHouseAttendee = async (
  openHouseUuid: string,
  primaryAgentUserId: string | number,
  attendee: any
) => {
  // This is intentionally the first asynchronous operation: never perform a
  // connectivity check or API request before the completed form is durable.
  const assertAccount = captureAccount();
  const persisted = await persistAttendee(openHouseUuid, attendee);
  const connection = await NetInfo.fetch().catch(() => null);
  const attemptImmediately =
    connection === null ||
    (connection.isConnected === true &&
      connection.isInternetReachable !== false);
  if (!attemptImmediately) {
    return { status: "queued" as const, attendees: persisted.attendees };
  }

  try {
    assertAccount();
    const attendees = await syncOpenHouseAttendees(
      openHouseUuid,
      primaryAgentUserId
    );
    const submitted = attendees.some(
      (attendee) =>
        attendee?.[LOCAL_ID] === persisted.attendee[LOCAL_ID] &&
        attendee?.is_synced
    );
    return {
      status: submitted ? ("submitted" as const) : ("queued" as const),
      attendees,
    };
  } catch (error) {
    console.error("Attendee submission queued after API failure:", {
      localSubmissionId: persisted.attendee[LOCAL_ID],
      status: (error as any)?.response?.status,
      response: (error as any)?.response?.data,
    });
    return { status: "queued" as const, attendees: persisted.attendees };
  }
};

export const syncOpenHouseAttendees = async (
  openHouseUuid: string,
  primaryAgentUserId: string | number
) => {
  const assertAccount = captureAccount();
  return withDeliveryLock(openHouseUuid, async () => {
    assertAccount();
    const attendees = await withStorageLock(async () => {
      const stored = await readAttendees(openHouseUuid);
      let changed = false;
      const migrated = stored.map((attendee) => {
        if (attendee?.uuid || attendee?.[LOCAL_ID]) return attendee;
        changed = true;
        return { ...attendee, [LOCAL_ID]: createLocalId() };
      });
      if (changed) await writeAttendees(openHouseUuid, migrated);
      return migrated;
    });
    const unsynced = attendees.filter(
      (attendee) => !attendee?.is_synced && !attendee?.uuid
    );

    let latest = attendees;
    const failures: unknown[] = [];
    for (const attendee of unsynced) {
      try {
        assertAccount();
        const remote = await remoteAttendees(openHouseUuid, assertAccount);
        assertAccount();
        const baseline = attendee[DELIVERY_BASELINE];
        if (Array.isArray(baseline)) {
          const candidates = remote.filter(entry => entry.uuid &&
            !baseline.includes(entry.uuid) && matches(attendee, entry));
          if (candidates.length > 1) throw new Error("Multiple matching saved leads require review");
          if (candidates.length === 0 && remote.some(entry => entry.uuid &&
            !baseline.includes(entry.uuid) && matchesAcknowledged(attendee, entry))) {
            throw new Error("Saved lead contact details are not yet available; retry after refresh");
          }
          if (candidates.length === 1) {
            latest = await markSynced(openHouseUuid, attendee[LOCAL_ID], candidates[0].uuid);
            continue;
          }
        } else {
          await withStorageLock(async () => {
            const stored = await readAttendees(openHouseUuid);
            const updated = stored.map(entry => entry[LOCAL_ID] === attendee[LOCAL_ID]
              ? { ...entry, [DELIVERY_BASELINE]: remote.map(entry => entry.uuid).filter(Boolean) }
              : entry);
            await writeAttendees(openHouseUuid, updated);
          });
        }
        assertAccount();
        await sendAttendee(openHouseUuid, primaryAgentUserId, attendee, assertAccount);
        assertAccount();
        latest = await markSynced(openHouseUuid, attendee[LOCAL_ID]);
      } catch (error) {
        console.error("Unable to replay attendee submission:", {
          localSubmissionId: attendee?.[LOCAL_ID],
          status: (error as any)?.response?.status,
          response: (error as any)?.response?.data,
        });
        // Session changes must abort immediately, even during partial delivery.
        assertAccount();
        failures.push(error);
      }
    }

    // Keep the rejection contract: completion cannot proceed with failed leads.
    // Re-throw the original cause so callers retain status/message classification.
    if (failures.length) throw failures[0];
    return latest;
  });
};

// Older queued completions carried their leads inside the completion payload.
// Restore any missing records once, then use the same durable delivery path.
export const prepareCompletionAttendees = (uuid: string, payload: any[] = []) =>
  withStorageLock(async () => {
    const stored = await readAttendees(uuid);
    const available = [...stored];
    for (const attendee of payload) {
      const index = available.findIndex(entry => matches(attendee, entry) ||
        JSON.stringify(apiPayload(attendee)) === JSON.stringify(apiPayload(entry)));
      if (index >= 0) available.splice(index, 1);
      else stored.push({ ...attendee, is_synced: false, [LOCAL_ID]: createLocalId() });
    }
    await writeAttendees(uuid, stored);
  });

// Delivery must finish before completion. Keep the cache after completion:
// deleting it would race new check-ins and removes offline attendee history.
export const completeOpenHouse = async (endpoint: string, data: any) => {
  const assertAccount = captureAccount();
  if (!data.primaryAgentUserId) throw new Error("An agent is required to complete the open house");
  await prepareCompletionAttendees(data.uuid, data.attendees);
  assertAccount();
  await syncOpenHouseAttendees(data.uuid, data.primaryAgentUserId);
  assertAccount();
  const response = await openhouseAPI.post(endpoint, { ...data, attendees: [] }, { timeout: REQUEST_TIMEOUT_MS, accountGuard: assertAccount } as any);
  assertAccount();
  return response;
};
